import * as React from 'react';
import styled from 'styled-components';
import { MathUtils, Quaternion, Vector2, Vector3 } from 'three';
import _ from 'underscore';

import { isDefined } from '../../../../../../../../utils/variables';
import cameraManager from '../../../../../../../../viewer/camera-manager';
import { SubscriptionTopic } from '../../../../../../../../viewer/messaging/pubsub';
import { SceneManager } from '../../../../../../../../viewer/scene-manager';
import { useSubscribe } from '../../../../../../../hooks/use-subscribe';

type CompassDirection =
  | 'N'
  | 'S'
  | 'E'
  | 'W'
  | 'NE'
  | 'NW'
  | 'SE'
  | 'SW'
  | 'NNE'
  | 'NNW'
  | 'ENE'
  | 'WNW'
  | 'SSE'
  | 'SSW'
  | 'ESE'
  | 'WSW'
  | 'N1'
  | 'N2'
  | 'N3'
  | 'N4'
  | 'E1'
  | 'E2'
  | 'E3'
  | 'E4'
  | 'S1'
  | 'S2'
  | 'S3'
  | 'S4'
  | 'W1'
  | 'W2'
  | 'W3'
  | 'W4';

interface CompassLabelProps {
  direction: CompassDirection;
  angle: number;
  isMainCardinal: boolean;
  opacity: number;
}

const CompassLabel: React.FC<CompassLabelProps> = ({
  direction,
  angle,
  isMainCardinal,
  opacity,
}) => {
  // Calculate position for ellipsoid rendering (squashed circle)
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const baseRadius = screenWidth * 0.2; // 20% of screen width

  // Create ellipsoid shape - squashed horizontally to emphasize forward direction
  // The ellipsoid should be wider horizontally and narrower vertically
  const horizontalRadius = baseRadius;
  const verticalRadius = baseRadius * 0.2; // 20% of horizontal radius for squashing

  // Calculate position on ellipsoid
  const x = -Math.sin(angle) * horizontalRadius;
  const y = -Math.cos(angle) * verticalRadius; // Negative Y to flip direction

  // Determine what to render based on direction type
  const renderContent = () => {
    if (isMainCardinal) {
      return <CardinalText>{direction}</CardinalText>;
    } else if (
      direction.includes('NNE') ||
      direction.includes('NNW') ||
      direction.includes('ENE') ||
      direction.includes('WNW') ||
      direction.includes('SSE') ||
      direction.includes('SSW') ||
      direction.includes('ESE') ||
      direction.includes('WSW')
    ) {
      // Tertiary cardinals - medium lines
      return <TertiaryLine />;
    } else if (
      direction.includes('NE') ||
      direction.includes('NW') ||
      direction.includes('SE') ||
      direction.includes('SW')
    ) {
      // Secondary cardinals - medium lines
      return <SecondaryLine />;
    }
  };

  return (
    <LabelContainer
      style={{
        opacity,
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
      }}
    >
      {renderContent()}
    </LabelContainer>
  );
};

// Helper function to get heading from quaternion (similar to poi.ts)
const getHeadingDegFromLocalQuat = (q: Quaternion): number => {
  // Sphere forward (+Z in its local space) expressed in parent-local frame.
  const f = new Vector3(0, 0, 1).applyQuaternion(q).normalize();
  // Project onto parent XZ-plane (yaw is around parent +Y).
  const fXZ = new Vector2(f.x, f.z);
  if (fXZ.lengthSq() < 1e-12) {
    // Degenerate, shouldn't happen in normal pano use.
    return 0;
  }

  // Angle from +Z toward +X (right-handed)
  const yawRad = Math.atan2(fXZ.x, fXZ.y);
  // Normalize to [0, 360)
  return (MathUtils.radToDeg(yawRad) + 360) % 360;
};

export const ScreenCompass: React.FC = () => {
  const [cameraHeading, setCameraHeading] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(
    SceneManager.instance.getActiveSceneType() === 'panoramaViewer',
  );
  const [, forceUpdate] = React.useState({});
  const [mouseDirection, setMouseDirection] = React.useState<number | null>(null);
  const compassRef = React.useRef<HTMLDivElement>(null);

  // Subscribe to camera movement
  // Use underscore throttle to limit calls to 20 per second (50ms interval)
  const throttledCameraUpdate = React.useMemo(
    () =>
      _.throttle(
        (cameraData: { camera: any }) => {
          const threeCamera = cameraData.camera.getTHREECamera();
          const headingDeg = getHeadingDegFromLocalQuat(threeCamera.quaternion as Quaternion);
          if (isDefined(cameraData?.camera)) {
            setCameraHeading(headingDeg);
          }
        },
        50,
        { leading: true, trailing: true },
      ),
    [],
  );

  useSubscribe(SubscriptionTopic.OnCameraMoved, (message) => throttledCameraUpdate(message));

  // Subscribe to scene changes to show/hide compass
  useSubscribe(SubscriptionTopic.OnSceneChangeStarted, (message: any) => {
    setIsVisible(message.scene === 'panoramaViewer');
  });

  // Subscribe to scene changes to show/hide compass
  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, (message: any) => {
    setIsVisible(message.scene === 'panoramaViewer');
  });

  // Handle window resize to update compass positioning
  React.useEffect(() => {
    const handleResize = () => {
      forceUpdate({});
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mouse movement to track direction anywhere on the screen
  React.useEffect(() => {
    if (!isVisible) {
      setMouseDirection(null);
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!compassRef.current) {
        return;
      }

      // Get the active camera
      const activeCamera = cameraManager().getActiveCamera();
      if (!activeCamera) {
        return;
      }

      const threeCamera = activeCamera.getTHREECamera();

      // Convert mouse screen coordinates to normalized device coordinates (-1 to +1)
      const mouseX = (event.clientX / window.innerWidth) * 2 - 1;
      const mouseY = -(event.clientY / window.innerHeight) * 2 + 1; // Invert Y axis

      // Create a vector from the camera through the mouse position
      const mouseVector = new Vector3(mouseX, mouseY, 0.5); // Z = 0.5 for near plane

      // Unproject the mouse position to get world direction
      mouseVector.unproject(threeCamera);

      // Get camera position
      const cameraPosition = new Vector3();
      threeCamera.getWorldPosition(cameraPosition);

      // Calculate direction vector from camera to mouse point
      const direction = mouseVector.sub(cameraPosition).normalize();

      // Convert 3D direction to compass angle
      // We need to transform the world direction to the camera's local coordinate system
      // and then calculate the relative angle from the camera's forward direction

      // Get camera's world matrix to transform direction to camera space
      const cameraWorldMatrix = threeCamera.matrixWorld;
      const cameraInverseMatrix = cameraWorldMatrix.clone().invert();

      // Transform the direction vector to camera's local space
      const localDirection = direction.clone().transformDirection(cameraInverseMatrix);

      // In camera's local space, forward is +Z, right is +X, up is +Y
      // We want the horizontal (yaw) component relative to the camera's forward direction
      const horizontalDirection = new Vector2(localDirection.x, localDirection.z);
      if (horizontalDirection.lengthSq() < 1e-12) {
        return;
      }
      horizontalDirection.normalize();

      // Calculate angle from forward direction (+Z) in camera's local space
      // atan2(x, z) gives us the angle from +Z axis (forward)
      const angle = Math.atan2(horizontalDirection.x, horizontalDirection.y);

      // Convert to degrees and normalize to [0, 360)
      const degrees = (MathUtils.radToDeg(angle) + 360) % 360;

      // Convert to radians for consistency with compass rotation
      setMouseDirection(MathUtils.degToRad(degrees));
    };

    // Add event listener to the entire window to track mouse anywhere
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  // Calculate compass directions based on camera heading
  // Convert heading from degrees to radians and invert so compass rotates opposite to camera
  const headingRad = MathUtils.degToRad(cameraHeading);
  const compassRotation = -headingRad;

  // Main cardinal directions
  const mainCardinals: CompassDirection[] = ['N', 'S', 'E', 'W'];

  // Secondary cardinal directions (8-point)
  const secondaryCardinals: CompassDirection[] = ['NE', 'NW', 'SE', 'SW'];

  // Tertiary cardinal directions (16-point)
  const tertiaryCardinals: CompassDirection[] = [
    'NNE',
    'NNW',
    'ENE',
    'WNW',
    'SSE',
    'SSW',
    'ESE',
    'WSW',
  ];

  // Calculate opacity based on position on ellipsoid
  const calculateOpacity = (angle: number) => {
    // Calculate position on ellipsoid
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const baseRadius = screenWidth * 0.4;
    const horizontalRadius = baseRadius;
    const verticalRadius = baseRadius * 0.2;

    const x = Math.sin(angle) * horizontalRadius;
    const y = -Math.cos(angle) * verticalRadius;

    // Hide labels on the bottom half of the ellipsoid (y > 0 means bottom half)
    if (y > 0) {
      return 0;
    }

    // Calculate distance from center (0, 0) on the ellipsoid
    const distanceFromCenter = Math.sqrt(x * x + y * y);

    // Calculate the maximum possible distance (at the edges of the ellipsoid)
    // This is the distance to the furthest point on the ellipsoid
    const maxDistance = Math.max(horizontalRadius, verticalRadius);

    // Normalize distance to [0, 1] range
    const normalizedDistance = distanceFromCenter / maxDistance;

    // Fade out when more than 70% from center (closer to edges)
    const fadeStart = 0.7;

    if (normalizedDistance > fadeStart) {
      return Math.max(0, 1 - (normalizedDistance - fadeStart) / (1 - fadeStart));
    }
    return 1;
  };

  return (
    <CompassContainer data-testid="compass-container" ref={compassRef}>
      {/* Main cardinal directions */}
      {mainCardinals.map((direction) => {
        const angle = getDirectionAngle(direction) + compassRotation;
        const opacity = calculateOpacity(angle);
        return (
          <CompassLabel
            angle={angle}
            direction={direction}
            isMainCardinal={true}
            key={direction}
            opacity={opacity}
          />
        );
      })}

      {/* Secondary cardinal directions (8-point) */}
      {secondaryCardinals.map((direction) => {
        const angle = getDirectionAngle(direction) + compassRotation;
        const opacity = calculateOpacity(angle);
        return (
          <CompassLabel
            angle={angle}
            direction={direction}
            isMainCardinal={false}
            key={direction}
            opacity={opacity}
          />
        );
      })}

      {/* Tertiary cardinal directions (16-point) */}
      {tertiaryCardinals.map((direction) => {
        const angle = getDirectionAngle(direction) + compassRotation;
        const opacity = calculateOpacity(angle);
        return (
          <CompassLabel
            angle={angle}
            direction={direction}
            isMainCardinal={false}
            key={direction}
            opacity={opacity}
          />
        );
      })}

      {/* Mouse direction indicator */}
      {mouseDirection !== null && <MouseDirectionIndicator angle={mouseDirection} />}

      {/* Camera direction indicator */}
      <CameraDirectionIndicator />
    </CompassContainer>
  );
};

// Mouse direction indicator component
const MouseDirectionIndicator: React.FC<{ angle: number }> = ({ angle }) => {
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const baseRadius = screenWidth * 0.2;
  const horizontalRadius = baseRadius;
  const verticalRadius = baseRadius * 0.2;

  // Calculate position on ellipsoid
  const x = Math.sin(angle) * horizontalRadius;
  const y = Math.cos(angle) * verticalRadius;

  return (
    <MouseIndicatorContainer
      style={{
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
      }}
    >
      <MousePointer />
    </MouseIndicatorContainer>
  );
};

// Camera direction indicator component
const CameraDirectionIndicator: React.FC = () => {
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const baseRadius = screenWidth * 0.2;
  const verticalRadius = baseRadius * 0.2;

  // Camera direction is always at the top center (North position) of the ellipsoid
  // This represents where the camera is looking on the compass
  const x = 0; // Center horizontally
  const y = -verticalRadius + 16; // Top of ellipsoid (North position)

  return (
    <CameraIndicatorContainer
      style={{
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
      }}
    >
      <CameraPointer />
    </CameraIndicatorContainer>
  );
};

const getDirectionAngle = (direction: CompassDirection): number => {
  const angleMap: Record<CompassDirection, number> = {
    // Main cardinals (4-point)
    N: 0,
    E: Math.PI / 2,
    S: Math.PI,
    W: (3 * Math.PI) / 2,

    // Secondary cardinals (8-point)
    NE: Math.PI / 4,
    SE: (3 * Math.PI) / 4,
    SW: (5 * Math.PI) / 4,
    NW: (7 * Math.PI) / 4,

    // Tertiary cardinals (16-point)
    NNE: Math.PI / 8,
    ENE: (3 * Math.PI) / 8,
    ESE: (5 * Math.PI) / 8,
    SSE: (7 * Math.PI) / 8,
    SSW: (9 * Math.PI) / 8,
    WSW: (11 * Math.PI) / 8,
    WNW: (13 * Math.PI) / 8,
    NNW: (15 * Math.PI) / 8,

    // Small intermediate directions (5 between each main cardinal)
    // North sector (0° to 90°)
    N1: Math.PI / 20, // 9°
    N2: Math.PI / 10, // 18°
    N3: (3 * Math.PI) / 20, // 27°
    N4: Math.PI / 5, // 36°

    // East sector (90° to 180°)
    E1: (11 * Math.PI) / 20, // 99°
    E2: (3 * Math.PI) / 10, // 108°
    E3: (13 * Math.PI) / 20, // 117°
    E4: (7 * Math.PI) / 10, // 126°

    // South sector (180° to 270°)
    S1: (21 * Math.PI) / 20, // 189°
    S2: (11 * Math.PI) / 10, // 198°
    S3: (23 * Math.PI) / 20, // 207°
    S4: (6 * Math.PI) / 5, // 216°

    // West sector (270° to 360°)
    W1: (31 * Math.PI) / 20, // 279°
    W2: (8 * Math.PI) / 5, // 288°
    W3: (33 * Math.PI) / 20, // 297°
    W4: (17 * Math.PI) / 10, // 306°
  };
  return angleMap[direction] || 0;
};

const CompassContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: 30vh;
  pointer-events: none;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 20px;
`;

const LabelContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: opacity 200ms ease;
  pointer-events: none;
  user-select: none;
`;

const CardinalText = styled.div`
  font-family: 'Arial', sans-serif;
  font-size: 1.2rem;
  font-weight: bold;
  color: #ffffff;
  text-shadow:
    2px 2px 4px rgba(0, 0, 0, 0.8),
    0 0 8px rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.3);
  padding: 0.2rem 0.4rem;
  border-radius: 0.3rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(2px);
`;

const SecondaryLine = styled.div`
  height: 1.2rem;
  width: 0.25em;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(255, 255, 255, 0.7) 20%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0.7) 80%,
    transparent 100%
  );
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
`;

const TertiaryLine = styled.div`
  height: 1rem;
  width: 0.2em;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 20%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0.6) 80%,
    transparent 100%
  );
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
`;

const MouseIndicatorContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  pointer-events: none;
  z-index: 1001;
  transition: transform 50ms ease-out;
`;

const MousePointer = styled.div`
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 16px solid #ff4444;
  filter: drop-shadow(0 0 4px rgba(255, 68, 68, 0.8));

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: -6px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 12px solid #ffffff;
  }
`;

const CameraIndicatorContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  pointer-events: none;
  z-index: 1002;
  transition: transform 100ms ease-out;
`;

const CameraPointer = styled.div`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 20px solid #00ff00;
  filter: drop-shadow(0 0 6px rgba(0, 255, 0, 0.8));

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: -8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 16px solid #ffffff;
  }
`;
